import { useEffect, useMemo, useState } from 'react';

import { padColumns } from 'utils/pad-columns';

import { OrderDetails } from '@pages/Orders/OrderDetails';
import type { Order } from 'api';
import { useGetCollectionOrdersFindByUuidOrExternalReference } from 'api';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';

import { getColumns } from 'components/pages/Orders/columns';

import { apiParams } from './apiAdapter';
import { ORDERS_SEARCH_RESULT_TEST_ID } from './constants';

import { SearchResults } from '../SearchResults';

type Props = {
  onSuccess: (order: Order | undefined) => void;
  searchParams: any;
};

export const OrdersSearchResults = ({ searchParams, onSuccess }: Props) => {
  const organisationId = useOrganisationId<number>();

  const {
    data: { data: orderRowData } = {},
    error,
    isFetching,
    isSuccess,
    refetch
  } = useGetCollectionOrdersFindByUuidOrExternalReference(
    organisationId,
    apiParams(searchParams)
  );

  const { findAccountByAccountNumber } = useVirtualAccounts();

  useEffect(() => {
    if (!isFetching && isSuccess) {
      onSuccess(orderRowData);
    }
  }, [isFetching, isSuccess, onSuccess, orderRowData]);

  const collectionOrdersColumns = useMemo(
    () => padColumns(getColumns(findAccountByAccountNumber)),
    [findAccountByAccountNumber]
  );

  const handleRowClick = (record: Order) => {
    setSelectedRowData((selectedRowData) =>
      selectedRowData !== record ? record : undefined
    );
  };

  const [selectedRowData, setSelectedRowData] = useState<Order | undefined>(
    undefined
  );

  useEffect(() => {
    setSelectedRowData(orderRowData);
  }, [orderRowData]);

  if (orderRowData && !error) {
    return (
      <SearchResults
        title="Collection Orders"
        idKey="uuid"
        detailComponent={
          selectedRowData && (
            <OrderDetails
              orderId={selectedRowData.uuid!}
              onClose={() => setSelectedRowData(undefined)}
              onTestCaseSuccess={refetch}
            />
          )
        }
        data={[orderRowData]}
        e2eId={ORDERS_SEARCH_RESULT_TEST_ID}
        isFetching={isFetching}
        columnsDefinition={collectionOrdersColumns}
        onRowClick={handleRowClick}
        isSelected={(record) => record.uuid === selectedRowData?.uuid}
      />
    );
  }
};
